import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ShieldCheck, Check, Loader2, ScanSearch, Flag, Star, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDED = [
  "Full scan of your Google Business profile reviews",
  "Flags reviews that violate Google's content policies",
  "Disputes and removes illegitimate bad reviews",
  "Tracks every dispute until resolution",
  "One-time fee — you own it, no subscription",
];

const STEPS = [
  { icon: ScanSearch, title: "Scan", desc: "ReviewShield reads every review on your Google Business profile." },
  { icon: Flag, title: "Dispute", desc: "Reviews that break Google's rules get flagged and disputed automatically." },
  { icon: Star, title: "Remove", desc: "Illegitimate bad reviews come down — your rating reflects your real work." },
];

export default function Software() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");
  const [buyState, setBuyState] = useState("idle");
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    (async () => {
      for (let i = 0; i < 20; i++) {
        try {
          const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            if (active) setPayment("paid");
            return;
          }
          if (["failed", "expired"].includes(data.payment_status)) {
            if (active) setPayment("failed");
            return;
          }
        } catch {}
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (active) setPayment("pending");
    })();
    return () => {
      active = false;
    };
  }, [sessionId]);

  const buy = async () => {
    setBuyState("loading");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "reviewshield_onetime",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      setBuyState("error");
    }
  };

  return (
    <>
      <Seo
        title="ReviewShield — Remove Bad Google Reviews | Covelli"
        siteName="Covelli"
        description="ReviewShield eliminates illegitimate bad reviews from your Google Business profile. One-time fee of $120 CAD — no subscription."
      />
      <section data-testid="software-hero" className="mx-auto max-w-7xl px-6 pb-6 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand"
        >
          Software by Covelli
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl md:text-6xl"
        >
          ReviewShield
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
        >
          The one-time software that eliminates bad reviews from your Google
          Business profile — so your rating reflects your real work, not your
          unluckiest day.
        </motion.p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 pt-8">
        <div className="grid gap-8 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3"
          >
            <div className="grid gap-5 sm:grid-cols-3">
              {STEPS.map((s, i) => (
                <div key={s.title} data-testid={`software-step-${i}`} className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <s.icon size={24} className="text-skybrand" />
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand">
                What's included
              </h2>
              <ul className="mt-6 space-y-4">
                {INCLUDED.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 text-base text-slate-700">
                    <Check size={17} className="mt-1 shrink-0 text-skybrand" />
                    {inc}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-2"
          >
            {payment === "paid" ? (
              <div
                data-testid="purchase-success-message"
                className="flex h-full flex-col items-center justify-center gap-4 rounded-2xl border border-skybrand-border bg-skybrand-light p-10 text-center"
              >
                <CheckCircle2 size={36} className="text-skybrand" />
                <p className="font-display text-xl font-bold text-slate-900">Purchase confirmed.</p>
                <p className="max-w-xs text-sm leading-relaxed text-slate-600">
                  Thank you — your ReviewShield access details are on the way to
                  your inbox. Questions? Email{" "}
                  <a href="mailto:team@covelli.ca" className="text-kinetic">team@covelli.ca</a>.
                </p>
              </div>
            ) : (
              <div className="flex h-full flex-col justify-center rounded-2xl border border-kinetic-border bg-kinetic-light p-8 md:p-10">
                <ShieldCheck size={30} className="text-kinetic" />
                <div className="mt-6 flex items-end gap-3">
                  <span className="font-display text-6xl font-extrabold tracking-tight text-kinetic">$120</span>
                  <span className="pb-2 font-mono text-xs uppercase tracking-widest text-slate-500">CAD</span>
                </div>
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-kinetic-hover">
                  One-time fee — no subscription, ever
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  Pay once and it's yours for good. Secure checkout by Stripe.
                </p>
                {sessionId && !payment && (
                  <p className="mt-4 font-mono text-xs uppercase tracking-widest text-skybrand-hover">
                    Confirming your payment…
                  </p>
                )}
                {payment === "failed" && (
                  <p data-testid="purchase-failed-message" className="mt-4 font-mono text-xs uppercase tracking-widest text-kinetic">
                    Payment didn't go through — try again.
                  </p>
                )}
                {canceled && (
                  <p data-testid="purchase-canceled-message" className="mt-4 font-mono text-xs uppercase tracking-widest text-slate-500">
                    Checkout canceled — no charge was made.
                  </p>
                )}
                {buyState === "error" && (
                  <p data-testid="purchase-error-message" className="mt-4 font-mono text-xs uppercase tracking-widest text-kinetic">
                    Something went wrong — please try again.
                  </p>
                )}
                <button
                  data-testid="buy-software-button"
                  onClick={buy}
                  disabled={buyState === "loading"}
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-kinetic px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover disabled:opacity-60"
                >
                  {buyState === "loading" ? <Loader2 size={16} className="animate-spin" /> : null}
                  Buy ReviewShield — $120
                </button>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  One-time payment · No subscription
                </p>
              </div>
            )}
          </motion.div>
        </div>

        <p className="mt-12 text-sm text-slate-500">
          Questions before you buy?{" "}
          <Link to="/contact" data-testid="software-contact-link" className="text-kinetic hover:text-kinetic-hover">
            Talk to us first
          </Link>{" "}
          — we'll tell you honestly if ReviewShield fits your situation.
        </p>
      </section>
    </>
  );
}
